<template>
	<div v-loading="loading" class="goods-channel-config">
		<el-collapse v-model="tierCollapse" class="tier-collapse">
			<el-collapse-item name="tier">
				<template slot="title">
					<span class="tier-collapse-title">
						阶梯毛利润配置
						<span v-if="isManual" class="tier-collapse-note">不适用（规则类型：手工指定）</span>
						<span v-else-if="isShopReadonly" class="tier-collapse-note">店铺通用规则，仅查看</span>
					</span>
				</template>
				<div v-if="isManual" class="tier-empty">当前规则类型为手工指定，无需配置阶梯毛利润。请在下方改一口价、勾选渠道后保存。</div>
				<div v-else-if="syncRule === 'none'" class="tier-empty">请先选择一口价或安全库存规则。</div>
				<div v-else class="tier-table" :class="{ 'tier-table--readonly': isShopReadonly }">
					<div class="tier-row tier-row--head" :class="{ 'tier-row--readonly': isShopReadonly }">
						<span>商品价格区间 (元)</span>
						<span>毛利润 (%)</span>
						<span v-if="!isShopReadonly">操作</span>
					</div>
					<div v-for="(item, index) in form.tierList" :key="item._uid" class="tier-row" :class="{ 'tier-row--readonly': isShopReadonly }">
						<div class="tier-range">
							<el-input-number v-model="item.priceMin" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" placeholder="最低价" size="small" />
							<span class="range-sep">&lt;=商品价格&lt;</span>
							<el-input-number v-model="item.priceMax" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" placeholder="最高价" size="small" />
						</div>
						<el-input-number v-model="item.profitRate" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" placeholder="毛利润(%)" size="small" />
						<button v-if="!isShopReadonly" type="button" class="text-link" @click="onRemoveTier(index)">删除</button>
					</div>
					<button v-if="!isShopReadonly" type="button" class="text-link tier-add" @click="onAddTier">+ 新增阶梯</button>
				</div>
			</el-collapse-item>
		</el-collapse>

		<el-alert
			v-if="isShopReadonly"
			:title="shopReadonlyTip"
			type="info"
			:closable="false"
			show-icon
			class="shop-alert"
		>
			<el-button type="text" size="mini" @click="jumpToShopRule">前往店铺配置</el-button>
		</el-alert>

		<el-form v-if="showRuleConfigForm" ref="form" :model="form" label-width="0" class="config-form" size="small">
			<div class="config-panel" :class="{ 'config-panel--readonly': isShopReadonly }">
				<div class="field-grid">
					<div class="field-item">
						<label>单款尺码毛利润 (%)</label>
						<el-input-number v-model="form.grossProfit" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
					</div>
					<div class="field-item">
						<label>渠道不支持退货代理价加点 (%)</label>
						<el-input-number v-model="form.nonReturnMarkup" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
					</div>
					<div class="field-item">
						<label>不退渠道折扣大于多少不推库存</label>
						<el-input-number v-model="form.nonReturnDiscount" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
					</div>
					<div class="field-item">
						<label>淘宝扣点数 (%)</label>
						<el-input-number v-model="form.pddDeduction" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
					</div>
					<div class="field-item">
						<label>运费 (元)</label>
						<el-input-number v-model="form.postage" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
					</div>
					<div class="field-item">
						<label>配货率 (%)</label>
						<el-input-number v-model="form.allocationRate" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
					</div>
					<div class="field-item">
						<label>准时率 (%)</label>
						<el-input-number v-model="form.onTimeRate" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
					</div>
					<div class="field-item">
						<label>预发日期</label>
						<el-select v-model="form.shipDays" :disabled="isShopReadonly" placeholder="请选择">
							<el-option v-for="item in shipDateOptions" :key="item.value" :label="item.label" :value="item.value" />
						</el-select>
					</div>
					<template v-if="isC3">
						<div class="field-item">
							<label>保价天数</label>
							<el-input-number v-model="form.priceProtectDays" :min="0" :precision="0" :step="1" :controls="false" :disabled="isShopReadonly" />
						</div>
						<div class="field-item">
							<label>价格上涨不更新价格(%)，范围内</label>
							<el-input-number v-model="form.priceUpInRange" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
						</div>
						<div class="field-item">
							<label>价格下跌不更新价格(%)，范围内</label>
							<el-input-number v-model="form.priceDownInRange" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
						</div>
						<div class="field-item">
							<label>价格上涨不更新价格(%)，范围外</label>
							<el-input-number v-model="form.priceUpOutRange" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
						</div>
						<div class="field-item">
							<label>价格下跌不更新价格(%)，范围外</label>
							<el-input-number v-model="form.priceDownOutRange" :min="0" :precision="2" :controls="false" :disabled="isShopReadonly" />
						</div>
						<div class="field-item">
							<label>安全价加价金额 (分)</label>
							<el-input-number v-model="form.singleBuyMarkup" :min="0" :precision="0" :step="1" :controls="false" :disabled="isShopReadonly" />
						</div>
					</template>
					<div v-if="isC3Product" class="field-item">
						<label>安全折扣</label>
						<el-input-number v-model="form.safetyDiscount" :min="0" :max="10" :precision="2" :step="0.01" :controls="false" />
						<span v-if="safetyDiscountHint" class="field-hint">{{ safetyDiscountHint }}</span>
					</div>
				</div>
				<div class="radio-section">
					<div class="radio-item">
						<label>撕防伪</label>
						<el-radio-group v-model="form.securityStatus" :disabled="isShopReadonly" size="mini" class="radio-compact">
							<el-radio label="all">全部</el-radio>
							<el-radio label="tear">撕防伪</el-radio>
							<el-radio label="no_tear">不撕防伪</el-radio>
						</el-radio-group>
					</div>
					<div class="radio-item radio-item--policy">
						<label>退货政策</label>
						<el-radio-group v-model="form.returnPolicy" :disabled="isShopReadonly" size="mini" class="radio-compact">
							<el-radio label="none">不做控制</el-radio>
							<el-radio label="exclude0_100">自动匹配渠道时，不匹配退货政策 0%+100% 的渠道</el-radio>
							<el-radio label="only100_0">自动匹配渠道时，只匹配退货政策 100%+0% 的渠道</el-radio>
						</el-radio-group>
					</div>
					<div class="radio-item">
						<label>渠道集</label>
						<el-radio-group v-model="form.channelSet" :disabled="isShopReadonly" size="mini" class="radio-compact">
							<el-radio label="all">全部</el-radio>
							<el-radio label="mandatory_backup">必选渠道+备选渠道</el-radio>
						</el-radio-group>
					</div>
				</div>
			</div>
		</el-form>

		<div class="rule-bar">
			<div class="rule-bar__label">规则类型</div>
			<el-radio-group v-model="syncRule" size="mini" class="rule-radio radio-compact" @change="onSyncRuleChange">
				<el-radio v-for="item in syncRuleOptions" :key="item.value" :label="item.value">{{ item.label }}</el-radio>
			</el-radio-group>
			<div class="rule-bar__actions">
				<el-button size="small" @click="$emit('close')">返回</el-button>
				<el-button type="primary" size="small" :loading="matching" :disabled="isManual || syncRule === 'none'" @click="onMatchChannels">按规则匹配渠道</el-button>
				<el-button type="primary" size="small" plain :loading="saving" :disabled="syncRule === 'none'" @click="onSaveAll">{{ saveButtonLabel }}</el-button>
			</div>
		</div>

		<div class="page-tips">
			<p>提示：绿色标识必选渠道；深色标识黑名单渠道；红色标识该渠道被规则过滤（配货率/准时率/预发/退货政策/安全库存与折扣），一般渠道不着色。</p>
			<p v-if="isManual">提示：点击渠道库存单元格勾选或取消绑定。勾选渠道名称可绑定该渠道全部尺码。保存会把一口价和库存一起推到淘宝。推送完成前请勿点「下载商品」，以免淘宝旧价覆盖本地改价。</p>
			<p v-else-if="isC2">提示：一口价选渠道只推库存不改价。表中一口价为仅试算，点「按规则匹配渠道」后看最高代理价和渠道选中变化，不落库、不推送。实际改价请用「手工指定」或「安全库存改价」。试价前如有淘宝后台改价请先「下载商品」保证基准价一致。匹配结果为预览，不保存渠道绑定。</p>
			<p v-else-if="isC3">提示：安全库存改价为库存+一口价联动。匹配结果为预览，不保存渠道绑定。单独规则可在表中改安全库存后先匹配再保存。</p>
			<p v-if="isC3">提示：安全折扣为 0 或未配置时所有渠道都不会匹配，请先配置安全库存和安全折扣（≥10 表示不限制；一般建议 8 折）。单独规则在本页填写后请先保存，再点「按规则匹配渠道」（保存后也会自动刷新匹配）。通用规则请到店铺「通用安全库存规则改价格设置」中配置。</p>
			<p>提示：尺码展示淘宝原值（如 165/80A）。鼠标移到库存单元格可查看不推送原因。合计为 0 不展示。</p>
		</div>

		<div v-if="!loading && !channelBlocks.length" class="channel-empty">
			暂无渠道库存数据。请先在店铺配置中设置渠道折扣
		</div>
		<div v-else-if="showC3UnmatchedHint" class="channel-empty channel-empty--hint">
			{{ c3UnmatchedHintText }}
		</div>
		<div v-for="(block, blockIndex) in channelBlocks" :key="blockIndex + '-' + syncRule" class="channel-block">
			<div class="channel-layout" :class="{ 'channel-layout--with-product': hasProductSide(block) }">
				<div v-if="hasProductSide(block)" class="channel-product-side">
					<div class="product-side-inner">
						<div v-if="block.product.pic || block.product.thumb_url || block.product.image_url" class="product-side-pic">
							<img :src="block.product.pic || block.product.thumb_url || block.product.image_url" alt="">
						</div>
						<div v-if="block.product.style_number" class="product-side-item"><span>型号</span>{{ block.product.style_number }}</div>
						<div v-if="block.product.brand" class="product-side-item"><span>品牌</span>{{ block.product.brand }}</div>
						<div v-if="block.product.color" class="product-side-item"><span>颜色</span>{{ block.product.color }}</div>
						<div v-if="block.product.price" class="product-side-item"><span>价格</span>{{ block.product.price }}</div>
					</div>
				</div>
				<div class="channel-table-wrap">
					<el-table
						:data="block.tablelist"
						border
						size="mini"
						class="channel-table"
						:cell-class-name="cellClassName"
						:span-method="function(opts) { return tableSpanMethod(opts, block) }"
					>
						<el-table-column
							:key="'shop-name-' + blockIndex + '-' + shopNameColumnWidth(block)"
							prop="shop_name"
							label="尺寸"
							:min-width="shopNameColumnWidth(block)"
							:width="shopNameColumnWidth(block)"
							fixed="left"
							align="center"
							show-overflow-tooltip
						>
							<template slot-scope="scope">
								<div class="channel-first-cell">
									<span v-if="canEdit && (scope.row._legend || isChannelDataRow(scope.row))" class="channel-checkbox-slot">
										<el-checkbox
											v-if="scope.row._legend"
											v-model="scope.row._checked"
											:indeterminate="scope.row._indeterminate"
											class="channel-row-checkbox"
											@change="onLegendCheckChange(blockIndex, scope.row, $event)"
										/>
										<el-checkbox
											v-else
											v-model="scope.row._checked"
											class="channel-row-checkbox"
											@change="onChannelRowCheck(blockIndex, scope.row, $event)"
										/>
									</span>
									<span v-if="scope.row._legend" class="channel-legend-label">渠道/组</span>
									<span v-else-if="scope.row._summary" class="summary-label">{{ summaryLabel(scope.row) }}</span>
									<span v-else class="channel-name-text">{{ scope.row.shop_name }}</span>
								</div>
							</template>
						</el-table-column>
						<el-table-column
							v-for="size in block.sizes"
							:key="String(size.id)"
							:prop="'size_' + size.id"
							min-width="76"
							width="76"
							align="center"
						>
							<template slot="header">
								<div v-if="hasParsedSizeKey(size.size_key)" class="size-header">
									<div class="size-header-key">{{ size.size_key }}</div>
									<div v-if="size.size && String(size.size) !== String(size.size_key)" class="size-header-size">{{ size.size }}</div>
								</div>
								<el-tooltip v-else content="未解析出尺码，请到SKU编码编辑" placement="top">
									<div class="size-header-empty">未解析</div>
								</el-tooltip>
							</template>
							<template slot-scope="scope">
								<p
									v-if="scope.row._legend && block.sizes.length && size.id === block.sizes[0].id"
									class="row-hint-text"
								>可用库存/订单锁定库存，库存=可用库存+订单锁定库存</p>
								<template v-else-if="scope.row._summary && isEditableSummaryCell(scope.row)">
									<el-input
										v-model="scope.row['size_' + size.id]"
										size="mini"
										class="cell-input"
										:placeholder="summaryCellPlaceholder(scope.row)"
									/>
								</template>
								<template v-else-if="scope.row._summary">
									<span class="summary-value">{{ formatSummaryCellValue(scope.row, scope.row['size_' + size.id]) }}</span>
								</template>
								<span
									v-else-if="canEdit && isChannelDataRow(scope.row)"
									class="size-cell-clickable"
									@click="onSizeCellClick(scope.row, size.id)"
								>
									<el-tooltip
										v-if="scope.row['size_' + size.id + '_tip']"
										:content="String(scope.row['size_' + size.id + '_tip'])"
										placement="top"
									>
										<span>{{ formatDisplayNumber(scope.row['size_' + size.id]) }}</span>
									</el-tooltip>
									<span v-else>{{ formatDisplayNumber(scope.row['size_' + size.id]) }}</span>
								</span>
								<span v-else>
									<el-tooltip
										v-if="isChannelDataRow(scope.row) && scope.row['size_' + size.id + '_tip']"
										:content="String(scope.row['size_' + size.id + '_tip'])"
										placement="top"
									>
										<span>{{ formatDisplayNumber(scope.row['size_' + size.id]) }}</span>
									</el-tooltip>
									<span v-else>{{ formatDisplayNumber(scope.row['size_' + size.id]) }}</span>
								</span>
							</template>
						</el-table-column>
						<el-table-column prop="stock" label="合计" min-width="52" align="center">
							<template slot-scope="scope">{{ formatStockTotal(scope.row.stock) }}</template>
						</el-table-column>
						<el-table-column prop="product_price" label="零售价" min-width="68" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : formatDisplayNumber(scope.row.product_price) }}</template>
						</el-table-column>
						<el-table-column prop="true_discount" label="折扣" min-width="58" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : displayCell(scope.row.true_discount) }}</template>
						</el-table-column>
						<el-table-column prop="agent_price" label="综合代理价/代理价" min-width="108" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : displayCell(scope.row.agent_price) }}</template>
						</el-table-column>
						<el-table-column prop="stop_take_time" label="截单" min-width="68" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : displayCell(scope.row.stop_take_time) }}</template>
						</el-table-column>
						<el-table-column prop="prev_send_delivery_date" label="预发" min-width="78" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : displayCell(scope.row.prev_send_delivery_date) }}</template>
						</el-table-column>
						<el-table-column prop="free_return_rate" label="退货比+扣款" min-width="96" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : displayCell(scope.row.free_return_rate) }}</template>
						</el-table-column>
						<el-table-column prop="allocation_rate" label="配货率" min-width="68" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : displayCell(scope.row.allocation_rate) }}</template>
						</el-table-column>
						<el-table-column prop="on_time_rate" label="准时率" min-width="68" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : displayCell(scope.row.on_time_rate) }}</template>
						</el-table-column>
						<el-table-column prop="security" label="撕防伪" min-width="72" align="center">
							<template slot-scope="scope">{{ scope.row._summary ? '' : formatSecurity(scope.row.security) }}</template>
						</el-table-column>
						<el-table-column prop="create_time_str" label="库存更新" min-width="88" align="center">
							<template slot-scope="scope">{{ scope.row._summary || scope.row._legend ? '' : displayCell(scope.row.create_time_str) }}</template>
						</el-table-column>
						<el-table-column v-if="showBatchColumn" label="批量配置" min-width="108" fixed="right" align="center">
							<template slot-scope="scope">
								<div v-if="(isManual || isC2) && scope.row._rowType === 'single_price'" class="batch-price-cell">
									<el-input v-model="batchPriceMap[blockIndex]" size="mini" class="cell-input" placeholder="批量" />
									<button type="button" class="text-link" @click="applyBatchFixedPrice(blockIndex)">应用</button>
								</div>
								<div v-else-if="scope.row._rowType === 'safety_stock'" class="batch-price-cell">
									<el-input v-model="batchStockMap[blockIndex]" size="mini" class="cell-input" placeholder="批量" />
									<button type="button" class="text-link" @click="applyBatchSafetyStock(blockIndex)">应用</button>
								</div>
							</template>
						</el-table-column>
					</el-table>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { mapChannelTableData } from '../detailed/retailGoodsChannelConfig'
	import { appendChannelLegendRow } from '../detailed/channelConfigTableMock'
	import {
		buildGoodsFixedPriceChannelSavePayload,
		buildGoodsSafetyStockPriceSavePayload,
		fetchPushConfig,
		mapFixedPriceChannelConfigToForm,
		mapSafetyStockPriceConfigToForm,
		validateProfitTierList,
	} from '../detailed/retailShopPushConfig'
	import {
		TB_GOOD_API,
		getRequestErrorMessage,
		isTbFixedPriceRule,
		isTbSafetyStockRule,
		isTbShopPushStrategy,
		isTbProductPushStrategy,
		resolvePushConfigType,
		resolveTbShopRulePath,
		resolveTbChannelMatchApi,
		buildTbChannelMatchPayload,
		hasParsedSizeKey,
	} from '@/constants/taobaoShop'
	import { getApiErrorMessage } from '@/constants/retailPlatform'

	let nextTierUid = 1
	const createTierRow = () => ({
		_uid: nextTierUid++,
		priceMin: undefined,
		priceMax: undefined,
		profitRate: undefined,
	})
	const DEFAULT_TIER_COUNT = 2
	const METRIC_HINT_COLUMN_COUNT = 11

	const DEFAULT_FORM = {
		grossProfit: 5,
		nonReturnMarkup: 0,
		nonReturnDiscount: 0,
		pddDeduction: 0,
		postage: 10,
		allocationRate: 90,
		onTimeRate: 90,
		shipDays: 3,
		returnPolicy: 'only100_0',
		securityStatus: 'all',
		channelSet: 'all',
		is_enabled: 1,
		singleBuyMarkup: 0,
		priceProtectDays: 0,
		priceUpInRange: 0,
		priceDownInRange: 0,
		priceUpOutRange: 0,
		priceDownOutRange: 0,
		safetyDiscount: 10,
		tierList: Array.from({ length: DEFAULT_TIER_COUNT }, createTierRow),
	}

	function isFlagOn(value) {
		return value === true || value === 1 || value === '1'
	}

	function hasRejected(row) {
		if (!row) {
			return false
		}
		const val = row.rejected_at
		return !(val == null || val === '' || val === 0 || val === '0' || val === false)
	}

	export default {
		name: 'TaobaoGoodsChannelShell',
		props: {
			productId: {
				type: [String, Number],
				default: '',
			},
			initialSyncRule: {
				type: String,
				default: '',
			},
			retailShopId: {
				type: [String, Number],
				default: '',
			},
		},
		data() {
			return {
				loading: false,
				saving: false,
				matching: false,
				tierCollapse: ['tier'],
				syncRule: this.initialSyncRule || 'none',
				form: JSON.parse(JSON.stringify(DEFAULT_FORM)),
				channelBlocks: [],
				batchStockMap: {},
				batchPriceMap: {},
				syncRuleOptions: [
					{ value: 'manual', label: '手工指定' },
					{ value: 'fixed_price_channel_shop', label: '一口价选渠道-通用规则' },
					{ value: 'fixed_price_channel_product', label: '一口价选渠道-单独规则' },
					{ value: 'safety_stock_price_shop', label: '通用安全库存规则改价格' },
					{ value: 'safety_stock_price_product', label: '单独安全库存规则改价格' },
					{ value: 'none', label: '未设置' },
				],
				shipDateOptions: [
					{ value: 1, label: '当日发货' },
				].concat(Array.from({ length: 14 }, (_, i) => ({
					value: i + 2,
					label: (i + 2) + '日内发货',
				}))),
			}
		},
		computed: {
			isManual() {
				return this.syncRule === 'manual'
			},
			isC2() {
				return isTbFixedPriceRule(this.syncRule)
			},
			isC3() {
				return isTbSafetyStockRule(this.syncRule)
			},
			isC3Product() {
				return this.syncRule === 'safety_stock_price_product'
			},
			isShopReadonly() {
				return isTbShopPushStrategy(this.syncRule)
			},
			canEdit() {
				return this.isManual
			},
			showBatchColumn() {
				return this.isManual || this.isC2 || this.isC3Product
			},
			showRuleConfigForm() {
				return this.isC2 || this.isC3
			},
			saveButtonLabel() {
				if (this.isManual) {
					return '保存'
				}
				if (this.isShopReadonly) {
					return '保存规则'
				}
				return '保存全部'
			},
			shopReadonlyTip() {
				return this.isC3
					? '当前使用店铺安全库存通用规则，参数请在店铺配置中修改。'
					: '当前使用店铺一口价通用规则，参数请在店铺配置中修改。'
			},
			safetyDiscountHint() {
				const val = this.form.safetyDiscount
				if (val == null || val === '') {
					return ''
				}
				if (Number(val) >= 10) {
					return '不限制'
				}
				return val + '折'
			},
			hasAnySelectedChannelSize() {
				const blocks = this.channelBlocks || []
				for (let i = 0; i < blocks.length; i++) {
					const sizes = blocks[i].sizes || []
					const rows = (blocks[i].tablelist || []).filter(this.isChannelDataRow)
					for (let r = 0; r < rows.length; r++) {
						for (let s = 0; s < sizes.length; s++) {
							if (rows[r]['size_' + sizes[s].id + '_select'] == 1) {
								return true
							}
						}
					}
				}
				return false
			},
			isSafetyDiscountUnset() {
				const val = this.form.safetyDiscount
				if (val == null || val === '') {
					return true
				}
				const num = Number(val)
				return isNaN(num) || num <= 0
			},
			showC3UnmatchedHint() {
				return !this.loading && this.isC3 && this.channelBlocks.length > 0 && !this.hasAnySelectedChannelSize
			},
			c3UnmatchedHintText() {
				if (this.isC3Product && !this.isSafetyDiscountUnset) {
					return '当前没有渠道被选中。安全折扣已填写，请点「按规则匹配渠道」按当前折扣刷新选中；若仍无选中，可能是该尺码渠道无库存或真实折扣仍高于安全折扣。'
				}
				if (this.isShopReadonly) {
					return '当前没有渠道被选中。通用规则的安全折扣为 0 或未配置时所有渠道都不会匹配，请到店铺「通用安全库存规则改价格设置」中配置后，重新打开本页或再点「按规则匹配渠道」。'
				}
				return '当前没有渠道被选中。安全折扣为 0 或未配置时所有渠道都不会匹配，请先填写安全折扣并保存，再点「按规则匹配渠道」。'
			},
			fetchKey() {
				return String(this.productId || '') + '|' + String(this.retailShopId || '')
			},
		},
		watch: {
			fetchKey: {
				immediate: true,
				handler() {
					this.syncRule = this.initialSyncRule || 'none'
					this.reloadAll()
				},
			},
		},
		methods: {
			hasParsedSizeKey,
			measureChannelNameWidth(text) {
				var w = 0
				var s = text != null ? String(text) : ''
				for (var i = 0; i < s.length; i++) {
					w += s.charCodeAt(i) > 255 ? 12 : 7
				}
				return w
			},
			shopNameColumnWidth(block) {
				var pad = this.canEdit ? 52 : 28
				var maxContentW = this.measureChannelNameWidth('渠道/组')
				var list = (block && block.tablelist) || []
				for (var i = 0; i < list.length; i++) {
					var row = list[i]
					var text = ''
					if (!row || row._legend) {
						text = '渠道/组'
					} else if (row._summary) {
						text = this.summaryLabel(row)
					} else {
						text = row.shop_name
					}
					var w = this.measureChannelNameWidth(text)
					if (w > maxContentW) {
						maxContentW = w
					}
				}
				var width = Math.ceil(maxContentW + pad)
				if (width < 100) {
					return 100
				}
				if (width > 220) {
					return 220
				}
				return width
			},
			displayCell(value) {
				if (value == null || value === '') {
					return '-'
				}
				return value
			},
			hasProductSide(block) {
				const product = block && block.product
				if (!product || typeof product !== 'object') {
					return false
				}
				return !!(product.pic || product.thumb_url || product.image_url || product.style_number || product.brand || product.color || product.price)
			},
			summaryLabel(row) {
				if (row && row._rowType === 'single_price') {
					return this.isC2 ? '一口价（仅试算）' : '一口价'
				}
				return (row && row.shop_name) || ''
			},
			formatSecurity(value) {
				if (value == 2 || value === 'tear') {
					return '撕防伪'
				}
				if (value == 3 || value === 'no_tear') {
					return '不撕防伪'
				}
				if (value == 1 || value === 'all') {
					return '全部'
				}
				return value == null || value === '' ? '-' : String(value)
			},
			formatDisplayNumber(value) {
				if (value == null || value === '') {
					return '0'
				}
				const num = Number(value)
				if (isNaN(num)) {
					return value
				}
				return String(Math.round(num))
			},
			formatYuanPrice(value) {
				if (value == null || value === '') {
					return '-'
				}
				const num = Number(value)
				if (isNaN(num)) {
					return value
				}
				return (Math.round(num * 100) / 100).toFixed(2)
			},
			formatSummaryCellValue(row, value) {
				if (row && row._rowType === 'safety_discount') {
					if (value == null || value === '') {
						return '-'
					}
					return Number(value).toFixed(2)
				}
				if (row && row._rowType === 'single_price') {
					return this.formatYuanPrice(value)
				}
				return this.formatDisplayNumber(value)
			},
			formatStockTotal(value) {
				if (value == null || value === '' || Number(value) === 0) {
					return ''
				}
				return this.formatDisplayNumber(value)
			},
			isChannelDataRow(row) {
				return !!(row && !row._summary && !row._legend)
			},
			isEditableSummaryCell(row) {
				if (!row || !row._summary) {
					return false
				}
				if (this.isManual || this.isC2) {
					return row._rowType === 'single_price'
				}
				return !!(this.isC3Product && row._rowType === 'safety_stock')
			},
			summaryCellPlaceholder(row) {
				if (row && row._rowType === 'single_price') {
					return this.isC2 ? '仅试算' : '一口价'
				}
				if (row && row._rowType === 'safety_stock') {
					return '安全库存'
				}
				return '请输入'
			},
			stripGroupPriceRows(blocks) {
				return (blocks || []).map((block) => {
					const tablelist = (block.tablelist || []).filter((row) => {
						return !(row && row._summary && row._rowType === 'group_price')
					})
					return Object.assign({}, block, { tablelist: tablelist })
				})
			},
			resetForm() {
				this.form = JSON.parse(JSON.stringify(DEFAULT_FORM))
			},
			assignMappedForm(mapped) {
				const form = JSON.parse(JSON.stringify(DEFAULT_FORM))
				Object.keys(mapped || {}).forEach((key) => {
					if (mapped[key] !== undefined && key !== 'tierList') {
						form[key] = mapped[key]
					}
				})
				if (mapped && mapped.tierList && mapped.tierList.length) {
					form.tierList = mapped.tierList.map((item) => Object.assign(createTierRow(), item))
				}
				this.form = form
			},
			reloadAll() {
				if (!this.productId) {
					return
				}
				this.channelBlocks = []
				if (this.syncRule === 'none') {
					this.resetForm()
					return
				}
				this.loading = true
				const tasks = []
				if (this.showRuleConfigForm) {
					tasks.push(this.loadPushConfig())
				} else {
					this.resetForm()
				}
				tasks.push(this.loadMatch().catch((err) => {
					console.log(err)
					this.channelBlocks = []
					this.$message.error(getRequestErrorMessage(err, '获取渠道匹配失败'))
				}))
				Promise.all(tasks).finally(() => {
					this.loading = false
				})
			},
			loadPushConfig() {
				const type = resolvePushConfigType(this.syncRule)
				if (this.isShopReadonly) {
					if (!this.retailShopId) {
						this.resetForm()
						return Promise.resolve()
					}
					return fetchPushConfig(this.$axios, Number(this.retailShopId), type).then((data) => {
						this.applyConfig(data)
					}).catch((err) => {
						console.log(err)
						this.resetForm()
						this.$message.error((err && err.message) || '获取店铺配置失败')
					})
				}
				return this.$axios.get(TB_GOOD_API.pushConfigGet, {
					params: {
						id: Number(this.productId),
						type: type,
					},
				}).then((res) => {
					if (res.data.code == 0) {
						this.applyConfig(res.data.data)
					} else {
						this.resetForm()
						this.$message.error(getApiErrorMessage(res, '获取配置失败'))
					}
				}).catch((err) => {
					console.log(err)
					this.resetForm()
					this.$message.error(getRequestErrorMessage(err, '获取配置失败'))
				})
			},
			applyConfig(data) {
				if (this.isC3) {
					const mapped = mapSafetyStockPriceConfigToForm(data, createTierRow, DEFAULT_TIER_COUNT)
					this.assignMappedForm((mapped && mapped.form) || mapped || {})
					const discount = this.readSafetyDiscount(data)
					if (discount != null) {
						this.form.safetyDiscount = discount
					}
				} else {
					this.assignMappedForm(mapFixedPriceChannelConfigToForm(data, createTierRow, DEFAULT_TIER_COUNT))
				}
			},
			readSafetyDiscount(data) {
				if (!data || typeof data !== 'object') {
					return null
				}
				if (Object.prototype.hasOwnProperty.call(data, 'safety_discount')) {
					return this.normalizeSafetyDiscount(data.safety_discount)
				}
				const nested = data.data
				if (nested && typeof nested === 'object' && Object.prototype.hasOwnProperty.call(nested, 'safety_discount')) {
					return this.normalizeSafetyDiscount(nested.safety_discount)
				}
				return null
			},
			normalizeSafetyDiscount(value) {
				if (value == null || value === '') {
					return null
				}
				const num = Number(value)
				if (isNaN(num)) {
					return null
				}
				return num
			},
			loadMatch(withWhatIf) {
				if (this.isManual) {
					return this.$axios.post(TB_GOOD_API.manualChannelMatch, {
						retail_shop_product_id: Number(this.productId),
					}).then((res) => this.applyMatchResponse(res))
				}
				if (!this.isC2 && !this.isC3) {
					this.channelBlocks = []
					return Promise.resolve()
				}
				const extra = {}
				if (withWhatIf && this.isC2) {
					const skuPrices = this.buildTrialSkuPrices()
					if (skuPrices.length) {
						extra.sku_prices = skuPrices
					}
				}
				if (withWhatIf && isTbProductPushStrategy(this.syncRule)) {
					extra.push_config = this.buildWhatIfPushConfig()
					if (this.isC3Product) {
						extra.sku_stocks = this.collectSkuStocks()
						extra.safety_discount = this.form.safetyDiscount
					}
				}
				return this.$axios.post(
					resolveTbChannelMatchApi(this.syncRule),
					buildTbChannelMatchPayload(this.productId, this.syncRule, extra)
				).then((res) => this.applyMatchResponse(res))
			},
			applyMatchResponse(res) {
				if (res.data.code == 0) {
					this.applyBlocks(res.data.data || {})
					return
				}
				this.channelBlocks = []
				throw new Error(res.data.message || res.data.msg || '获取渠道匹配失败')
			},
			applyBlocks(data) {
				const mapped = mapChannelTableData(data, this.syncRule || 'manual')
				let blocks = this.stripGroupPriceRows(mapped.channelBlocks || [])
				this.channelBlocks = this.canEdit ? appendChannelLegendRow(blocks) : blocks
				if (this.canEdit) {
					this.initRowChecks()
				}
			},
			buildWhatIfPushConfig() {
				if (this.isC3) {
					const payload = buildGoodsSafetyStockPriceSavePayload(this.productId, this.form)
					const data = Object.assign({}, payload.data)
					delete data.safety_stocks
					delete data.sku_stocks
					return data
				}
				return buildGoodsFixedPriceChannelSavePayload(this.productId, this.form).data
			},
			collectSkuStocks() {
				const stocks = []
				this.channelBlocks.forEach((block) => {
					const row = (block.tablelist || []).find((item) => item._summary && item._rowType === 'safety_stock')
					;(block.sizes || []).forEach((size) => {
						const value = row && row['size_' + size.id]
						if (value == null || value === '') {
							return
						}
						stocks.push({
							id: Number(size.id),
							safety_stock: Number(value) || 0,
						})
					})
				})
				return stocks
			},
			initRowChecks() {
				this.channelBlocks.forEach((block, blockIndex) => {
					;(block.tablelist || []).forEach((row) => {
						if (row._summary) {
							return
						}
						if (row._checked === undefined) {
							this.$set(row, '_checked', this.isRowFullySelected(block, row))
						}
						if (row._legend && row._indeterminate === undefined) {
							this.$set(row, '_indeterminate', false)
						}
					})
					this.syncLegendCheck(blockIndex)
				})
			},
			isRowFullySelected(block, row) {
				if (!this.isChannelDataRow(row)) {
					return false
				}
				const sizes = block.sizes || []
				if (!sizes.length) {
					return false
				}
				return sizes.every((size) => row['size_' + size.id + '_select'] == 1)
			},
			setRowSizes(block, row, selected) {
				;(block.sizes || []).forEach((size) => {
					this.$set(row, 'size_' + size.id + '_select', selected ? 1 : 0)
				})
			},
			onSizeCellClick(row, sizeId) {
				if (!this.canEdit || !this.isChannelDataRow(row)) {
					return
				}
				const selectKey = 'size_' + sizeId + '_select'
				this.$set(row, selectKey, row[selectKey] == 1 ? 0 : 1)
				const blockIndex = this.channelBlocks.findIndex((block) => (block.tablelist || []).indexOf(row) !== -1)
				if (blockIndex >= 0) {
					const block = this.channelBlocks[blockIndex]
					this.$set(row, '_checked', this.isRowFullySelected(block, row))
					this.syncLegendCheck(blockIndex)
				}
			},
			onChannelRowCheck(blockIndex, row, checked) {
				if (!this.canEdit) {
					return
				}
				const block = this.channelBlocks[blockIndex]
				if (!block) {
					return
				}
				this.setRowSizes(block, row, !!checked)
				this.syncLegendCheck(blockIndex)
			},
			onLegendCheckChange(blockIndex, row, checked) {
				if (!this.canEdit) {
					return
				}
				const block = this.channelBlocks[blockIndex]
				if (!block) {
					return
				}
				this.$set(row, '_indeterminate', false)
				;(block.tablelist || []).forEach((item) => {
					if (!this.isChannelDataRow(item)) {
						return
					}
					this.$set(item, '_checked', !!checked)
					this.setRowSizes(block, item, !!checked)
				})
			},
			syncLegendCheck(blockIndex) {
				const block = this.channelBlocks[blockIndex]
				if (!block) {
					return
				}
				const legend = (block.tablelist || []).find((row) => row._legend)
				if (!legend) {
					return
				}
				const channels = (block.tablelist || []).filter(this.isChannelDataRow)
				const checkedCount = channels.filter((row) => row._checked).length
				this.$set(legend, '_checked', checkedCount > 0 && checkedCount === channels.length)
				this.$set(legend, '_indeterminate', checkedCount > 0 && checkedCount < channels.length)
			},
			cellClassName({ row, column }) {
				const classes = []
				if (row._summary) {
					classes.push('summary-row-cell')
				}
				if (row._legend) {
					classes.push('legend-row-cell')
				}
				if (this.isChannelDataRow(row)) {
					classes.push('channel-data-row-cell')
					if (isFlagOn(row.is_blacklisted)) {
						classes.push('channel-row--blacklist')
					} else if (hasRejected(row)) {
						classes.push('channel-row--rejected')
					} else if (isFlagOn(row.is_mandatory)) {
						classes.push('channel-row--mandatory')
					}
				}
				if (column.property && column.property.indexOf('size_') === 0) {
					const selectKey = column.property + '_select'
					if (this.isChannelDataRow(row) && row[selectKey] == 1) {
						classes.push('size-selected')
					} else if (row._summary) {
						classes.push('size-summary')
					}
				}
				return classes.join(' ')
			},
			tableSpanMethod({ row, columnIndex }, block) {
				const sizeCount = block && block.sizes ? block.sizes.length : 0
				const batchOffset = this.showBatchColumn ? 1 : 0
				const legendRestColspan = sizeCount + METRIC_HINT_COLUMN_COUNT + batchOffset
				if (row._legend) {
					if (columnIndex === 0) {
						return { rowspan: 1, colspan: 1 }
					}
					if (columnIndex === 1) {
						return { rowspan: 1, colspan: legendRestColspan }
					}
					if (columnIndex > 1 && columnIndex < 1 + legendRestColspan) {
						return { rowspan: 0, colspan: 0 }
					}
				}
				return { rowspan: 1, colspan: 1 }
			},
			applyBatchSafetyStock(blockIndex) {
				const block = this.channelBlocks[blockIndex]
				const row = block && (block.tablelist || []).find((item) => item._summary && item._rowType === 'safety_stock')
				const value = this.batchStockMap[blockIndex]
				if (!row || value == null || value === '') {
					return
				}
				;(block.sizes || []).forEach((size) => {
					this.$set(row, 'size_' + size.id, value)
				})
			},
			applyBatchFixedPrice(blockIndex) {
				const price = this.normalizeYuanPrice(this.batchPriceMap[blockIndex])
				if (price == null || price <= 0) {
					this.$message.warning('请输入大于 0 的一口价')
					return
				}
				const block = this.channelBlocks[blockIndex]
				const row = block && (block.tablelist || []).find((item) => item._summary && item._rowType === 'single_price')
				if (!row) {
					return
				}
				;(block.sizes || []).forEach((size) => {
					if (!hasParsedSizeKey(size.size_key)) {
						return
					}
					this.$set(row, 'size_' + size.id, price)
				})
			},
			normalizeYuanPrice(value) {
				if (value == null || value === '') {
					return null
				}
				const num = Number(value)
				if (isNaN(num)) {
					return null
				}
				return Math.round(num * 100) / 100
			},
			findSummaryRow(block, rowType) {
				if (!block || !Array.isArray(block.tablelist)) {
					return null
				}
				return (block.tablelist || []).find((row) => row && row._summary && row._rowType === rowType) || null
			},
			buildTrialSkuPrices() {
				const skuPrices = []
				this.channelBlocks.forEach((block) => {
					const sizes = block.sizes || []
					const priceRow = this.findSummaryRow(block, 'single_price')
					sizes.forEach((size) => {
						const id = Number(size.id)
						if (!id || !hasParsedSizeKey(size.size_key)) {
							return
						}
						const price = this.normalizeYuanPrice(priceRow ? priceRow['size_' + size.id] : undefined)
						if (price == null || price <= 0) {
							return
						}
						skuPrices.push({
							id: id,
							price: price,
						})
					})
				})
				return skuPrices
			},
			hasInvalidTrialPrice() {
				let invalid = false
				this.channelBlocks.forEach((block) => {
					const sizes = block.sizes || []
					const priceRow = this.findSummaryRow(block, 'single_price')
					sizes.forEach((size) => {
						if (!hasParsedSizeKey(size.size_key)) {
							return
						}
						const raw = priceRow ? priceRow['size_' + size.id] : undefined
						if (raw == null || raw === '') {
							return
						}
						const price = this.normalizeYuanPrice(raw)
						if (price == null || price <= 0) {
							invalid = true
						}
					})
				})
				return invalid
			},
			buildSkuPrices() {
				const skuPrices = []
				this.channelBlocks.forEach((block) => {
					const sizes = block.sizes || []
					const priceRow = this.findSummaryRow(block, 'single_price')
					sizes.forEach((size) => {
						const id = Number(size.id)
						if (!id) {
							return
						}
						skuPrices.push({
							id: id,
							price: this.normalizeYuanPrice(priceRow ? priceRow['size_' + size.id] : undefined),
						})
					})
				})
				return skuPrices
			},
			validateManualSave(skuPrices, bindings) {
				if (!skuPrices.length) {
					this.$message.warning('没有可保存的 SKU 一口价，请先核对编码与款号')
					return false
				}
				for (let i = 0; i < skuPrices.length; i++) {
					if (skuPrices[i].price == null || skuPrices[i].price <= 0) {
						this.$message.warning('SKU 一口价必须大于 0')
						return false
					}
				}
				if (!bindings.length) {
					this.$message.warning('请至少勾选一条渠道后再保存')
					return false
				}
				return true
			},
			onAddTier() {
				this.form.tierList.push(createTierRow())
			},
			onRemoveTier(index) {
				if (this.form.tierList.length <= 1) {
					this.$message.warning('至少保留一条阶梯')
					return
				}
				this.form.tierList.splice(index, 1)
			},
			resolveRowShopId(row) {
				if (row.shop_id != null && row.shop_id !== '') {
					return Number(row.shop_id)
				}
				if (row.user_shop_id != null && row.user_shop_id !== '') {
					return Number(row.user_shop_id)
				}
				return null
			},
			buildBindings() {
				const bindings = []
				this.channelBlocks.forEach((block) => {
					const sizes = block.sizes || []
					;(block.tablelist || []).forEach((row) => {
						if (!this.isChannelDataRow(row)) {
							return
						}
						const shopId = this.resolveRowShopId(row)
						if (shopId == null || isNaN(shopId)) {
							return
						}
						sizes.forEach((size) => {
							if (row['size_' + size.id + '_select'] != 1) {
								return
							}
							bindings.push({
								retail_shop_product_sku_id: size.id,
								user_shop_id: shopId,
							})
						})
					})
				})
				return bindings
			},
			jumpToShopRule() {
				const shopId = this.retailShopId
				const path = resolveTbShopRulePath(this.syncRule)
				if (!shopId) {
					this.$message.warning('缺少店铺信息')
					return
				}
				if (!path) {
					this.$message.warning('当前规则暂不支持跳转店铺配置')
					return
				}
				sessionStorage.setItem('retailShopId', String(shopId))
				sessionStorage.setItem('storeConfigPlatform', 'taobao')
				this.$router.push({
					path: path,
					query: { id: shopId },
				})
			},
			persistSyncRule() {
				return this.$axios.post(TB_GOOD_API.pushStrategySave, {
					id: Number(this.productId),
					push_strategy: this.syncRule,
				}).then((res) => {
					if (res.data.code != 0) {
						throw new Error(res.data.message || res.data.msg || '更新同步规则失败')
					}
					this.$emit('rule-change', this.syncRule)
				})
			},
			onSyncRuleChange() {
				this.persistSyncRule().then(() => {
					this.reloadAll()
				}).catch((err) => {
					console.log(err)
					this.$message.error((err && err.message) || getRequestErrorMessage(err, '更新同步规则失败'))
				})
			},
			onMatchChannels() {
				if (this.isC2 && this.hasInvalidTrialPrice()) {
					this.$message.warning('SKU 一口价必须大于 0 才会参与试算')
					return
				}
				this.matching = true
				this.loadMatch(true).then(() => {
					this.$message.success(this.isC2 ? '已按规则匹配渠道（一口价仅试算，未保存）' : '已按规则匹配渠道')
				}).catch((err) => {
					console.log(err)
					this.channelBlocks = []
					this.$message.error((err && err.message) || getRequestErrorMessage(err, '获取渠道匹配失败'))
				}).finally(() => {
					this.matching = false
				})
			},
			onSaveAll() {
				if (this.syncRule === 'none') {
					return
				}
				if (this.showRuleConfigForm && !this.isShopReadonly) {
					const tierCheck = validateProfitTierList(this.form.tierList)
					if (!tierCheck.valid) {
						this.$message.error(tierCheck.message)
						return
					}
				}
				let skuPrices = []
				let skuShopBindings = []
				if (this.isManual) {
					skuPrices = this.buildSkuPrices()
					skuShopBindings = this.buildBindings()
					if (!this.validateManualSave(skuPrices, skuShopBindings)) {
						return
					}
				}
				this.saving = true
				this.persistSyncRule().then(() => {
					if (this.isManual) {
						return this.$axios.post(TB_GOOD_API.manualSave, {
							retail_shop_product_id: Number(this.productId),
							sku_prices: skuPrices,
							sku_shop_bindings: skuShopBindings,
						}).then((res) => {
							if (res.data.code != 0) {
								throw new Error(res.data.message || res.data.msg || '保存失败')
							}
							this.$message.success('保存成功。价格与库存已提交同步，约 3～5 秒后可在 Sku更新记录 查看。推送完成前请勿下载商品。')
							this.loadMatch()
						})
					}
					if (this.isShopReadonly) {
						this.$message.success(this.isC2
							? '同步规则已更新。参数请在店铺配置中修改。一口价试算未保存、未推送，实际改价请用「手工指定」或「安全库存改价」。'
							: '同步规则已更新。参数请在店铺配置中修改')
						return
					}
					let data
					try {
						if (this.isC3) {
							const payload = buildGoodsSafetyStockPriceSavePayload(this.productId, this.form)
							data = Object.assign({}, payload.data)
							delete data.safety_stocks
							data.safety_discount = this.form.safetyDiscount != null ? Number(this.form.safetyDiscount) : 10
							data.sku_stocks = this.collectSkuStocks()
						} else {
							data = buildGoodsFixedPriceChannelSavePayload(this.productId, this.form).data
						}
					} catch (err) {
						this.$message.error((err && err.message) || '配置校验失败')
						return
					}
					return this.$axios.post(TB_GOOD_API.pushConfigSave, {
						id: Number(this.productId),
						type: resolvePushConfigType(this.syncRule),
						data: data,
					}).then((res) => {
						if (res.data.code != 0) {
							throw new Error(res.data.message || res.data.msg || '保存失败')
						}
						if (this.isC2) {
							this.$message.success('配置已保存。一口价试算未保存、未推送，实际改价请用「手工指定」或「安全库存改价」。')
							return
						}
						this.$message.success(this.isC3 ? '配置已保存，正在按当前规则刷新渠道匹配' : '配置已保存')
						if (this.isC3) {
							return this.loadMatch()
						}
					})
				}).catch((err) => {
					console.log(err)
					this.$message.error((err && err.message) || getRequestErrorMessage(err, '保存失败'))
				}).finally(() => {
					this.saving = false
				})
			},
		},
	}
</script>

<style scoped lang="less">
	.goods-channel-config {
		padding: 16px 18px 24px;
		font-size: 13px;
		color: #303133;
	}

	.tier-collapse {
		margin-bottom: 12px;
		border: none;

		/deep/ .el-collapse-item__header {
			height: 38px;
			line-height: 38px;
			font-size: 13px;
			color: #606266;
			background: #f7f8fa;
			border: 1px solid #eef0f3;
			border-radius: 6px;
			padding: 0 12px;
		}

		/deep/ .el-collapse-item__wrap {
			border: none;
		}

		/deep/ .el-collapse-item__content {
			padding: 10px 0 0;
		}
	}

	.tier-collapse-note {
		margin-left: 8px;
		font-size: 12px;
		color: #909399;
	}

	.tier-empty {
		padding: 8px 0;
		color: #909399;
		font-size: 12px;
	}

	.shop-alert {
		margin-bottom: 12px;
	}

	.tier-table {
		border: 1px solid #eef0f3;
		border-radius: 6px;
		overflow: hidden;
	}

	.tier-row {
		display: grid;
		grid-template-columns: 1fr 120px 56px;
		gap: 8px;
		align-items: center;
		padding: 8px 10px;
		border-bottom: 1px solid #f0f2f5;

		&--head {
			background: #fafbfc;
			color: #909399;
			font-size: 12px;
		}

		&--readonly {
			grid-template-columns: 1fr 120px;
		}
	}

	.tier-range {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;
	}

	.range-sep {
		color: #909399;
		font-size: 12px;
		white-space: nowrap;
	}

	.tier-add,
	.text-link {
		border: none;
		background: none;
		color: #ff4714;
		cursor: pointer;
		font-size: 12px;
		padding: 0;
	}

	.tier-add {
		margin: 8px 10px;
	}

	.config-panel {
		margin-bottom: 14px;
		padding: 12px;
		border: 1px solid #eef0f3;
		border-radius: 6px;

		&--readonly {
			background: #fafbfc;
		}
	}

	.field-grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 10px 16px;
	}

	.field-item {
		label {
			display: block;
			margin-bottom: 4px;
			font-size: 12px;
			color: #606266;
		}

		.el-input-number,
		.el-select {
			width: 100%;
		}
	}

	.field-hint {
		display: block;
		margin-top: 4px;
		font-size: 11px;
		color: #909399;
	}

	.radio-section {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-top: 14px;
		padding-top: 14px;
		border-top: 1px solid #f0f2f5;
	}

	.radio-item {
		display: flex;
		flex-direction: column;
		gap: 6px;
		min-width: 0;

		label {
			font-size: 12px;
			color: #909399;
			line-height: 1.4;
			flex-shrink: 0;
		}

		&--policy {
			/deep/ .el-radio {
				display: flex;
				align-items: flex-start;
				white-space: normal;
				height: auto;
				line-height: 1.4;
				margin-right: 12px;
				margin-bottom: 2px;
			}

			/deep/ .el-radio__label {
				white-space: normal;
				line-height: 1.4;
			}
		}
	}

	.radio-compact {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 2px 0;

		/deep/ .el-radio {
			margin-right: 12px;
			margin-bottom: 2px;
			line-height: 1.4;
		}

		/deep/ .el-radio__label {
			font-size: 12px;
			padding-left: 5px;
			color: #606266;
		}

		/deep/ .el-radio__inner {
			width: 13px;
			height: 13px;

			&::after {
				width: 5px;
				height: 5px;
			}
		}
	}

	.rule-bar {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
		padding: 12px;
		margin-bottom: 12px;
		background: #fafbfc;
		border: 1px solid #eef0f3;
		border-radius: 6px;
	}

	.rule-bar__label {
		font-size: 12px;
		color: #909399;
		flex-shrink: 0;
	}

	.rule-radio {
		flex: 1;
		min-width: 0;
	}

	.rule-bar__actions {
		margin-left: auto;
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
	}

	.page-tips {
		margin-bottom: 12px;
		padding: 8px 10px;
		background: #fff7f5;
		border-radius: 6px;

		p {
			margin: 0;
			font-size: 12px;
			line-height: 1.6;
			color: #c45656;

			+ p {
				margin-top: 4px;
			}
		}
	}

	.channel-empty {
		padding: 24px;
		text-align: center;
		color: #909399;
		background: #fafbfc;
		border-radius: 6px;

		&--hint {
			margin-bottom: 12px;
			color: #e6a23c;
			background: #fdf6ec;
		}
	}

	.channel-block {
		margin-bottom: 14px;
	}

	.channel-layout {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		border: 1px solid #eef0f3;
		border-radius: 6px;
		overflow: hidden;

		&--with-product {
			grid-template-columns: 132px minmax(0, 1fr);
		}
	}

	.channel-product-side {
		padding: 8px;
		background: #fafbfc;
		border-right: 1px solid #eef0f3;
		font-size: 12px;
		color: #606266;
	}

	.product-side-pic {
		width: 100%;
		height: 112px;
		margin-bottom: 6px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: #fff;
		border: 1px solid #eef0f3;
		border-radius: 4px;
		overflow: hidden;

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	.product-side-item {
		padding: 4px 0;
		border-bottom: 1px solid #f0f2f5;

		span {
			display: block;
			color: #909399;
			font-size: 11px;
		}
	}

	.channel-table-wrap {
		min-width: 0;
		overflow: auto;
	}

	.channel-table {
		font-size: 11px;

		/deep/ th {
			background: #f7f8fa !important;
			color: #909399;
			font-weight: 500;
		}

		/deep/ .size-selected {
			background: #fff7ed !important;
		}

		/deep/ .size-summary,
		/deep/ .summary-row-cell {
			background: #fafbfc !important;
		}

		/deep/ .legend-row-cell {
			background: #fff !important;
		}

		/deep/ .channel-row--mandatory {
			background: #f0f9eb !important;
		}

		/deep/ .channel-row--rejected {
			background: #fef0f0 !important;
			color: #c45656;
		}

		/deep/ .channel-row--blacklist {
			background: #303133 !important;
			color: #f5f7fa;
		}
	}

	.channel-first-cell {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		min-width: 0;
	}

	.channel-legend-label,
	.summary-label,
	.channel-name-text {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.size-cell-clickable {
		display: inline-block;
		width: 100%;
		cursor: pointer;
	}

	.size-header {
		line-height: 1.3;
	}

	.size-header-key {
		color: #303133;
		font-weight: 500;
	}

	.size-header-size {
		margin-top: 2px;
		font-size: 11px;
		color: #909399;
		font-weight: normal;
	}

	.size-header-empty {
		font-size: 11px;
		line-height: 1.35;
		color: #f56c6c;
		font-weight: normal;
		white-space: nowrap;
		cursor: default;
	}

	.row-hint-text {
		margin: 0;
		text-align: left;
		font-size: 12px;
		color: #909399;
		line-height: 1.5;
	}

	.cell-input {
		width: 100%;
	}

	.batch-price-cell {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
	}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/taobao/goodsChannelShell.vue